import React from "react";

import { Button, Modal, ModalBody, ModalFooter } from "@/Components/ui";
import { DialogDescription, DialogTitle } from "@/Components/ui/shadcn/dialog";
import { cn } from "@/lib/utils";
import { DialogTone } from "@/utils/dialogService";

const toneTitleClassMap: Record<DialogTone, string> = {
  default: "text-slate-900",
  warning: "text-amber-700",
  error: "text-red-700",
  destructive: "text-red-700",
};

const toneButtonColorMap: Record<DialogTone, string> = {
  default: "primary",
  warning: "warning",
  error: "danger",
  destructive: "danger",
};

export interface CommonDialogAction {
  label: string;
  onClick?: () => void | Promise<void>;
  color?: string;
  disabled?: boolean;
  closeOnClick?: boolean;
}

export interface CommonDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  tone?: DialogTone;
  children?: React.ReactNode;
  actions?: CommonDialogAction[];
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
  centered?: boolean;
}

const defaultActions: CommonDialogAction[] = [];

const CommonDialog = ({
  isOpen,
  onClose,
  title = "",
  description = "",
  tone = "default",
  children,
  actions = defaultActions,
  size = "md",
  centered = true,
}: CommonDialogProps) => {
  const handleActionClick = async (action: CommonDialogAction) => {
    await action.onClick?.();
    if (action.closeOnClick !== false) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      variant="dialog"
      centered={centered}
      backdrop="static"
    >
      <ModalBody className="px-5 py-4">
        <div className="text-center">
          {title ? (
            <DialogTitle className={cn("text-center text-xl", toneTitleClassMap[tone])}>
              {title}
            </DialogTitle>
          ) : null}
          {description ? (
            <DialogDescription className="mt-2 whitespace-pre-line text-muted">
              {description}
            </DialogDescription>
          ) : null}
          {children ? <div className="mt-4">{children}</div> : null}
        </div>
      </ModalBody>
      {actions.length > 0 ? (
        <ModalFooter className="justify-center">
          {actions.map((action, index) => (
            <Button
              key={`${action.label}-${index}`}
              type="button"
              color={action.color ?? toneButtonColorMap[tone]}
              disabled={action.disabled}
              onClick={() => {
                void handleActionClick(action);
              }}
            >
              {action.label}
            </Button>
          ))}
        </ModalFooter>
      ) : null}
    </Modal>
  );
};

export default CommonDialog;

